<template>
  <div class="cac">
    <div class="cacHead">
      <span class="cacTitle">{{ headline }}</span>
      <span class="cacSub">{{ subline }}</span>
    </div>

    <!-- The code is shown here and typed on the phone — there is deliberately
         no code-entry field in the app (design 1e). An already-verified number
         short-circuits with no code at all, so the grid is code-gated rather than
         phase-gated — otherwise it renders six blank tiles. -->
    <div v-if="code && (phase === 'waiting' || phase === 'verified')" class="tiles">
      <span v-for="(ch, i) in codeChars" :key="i" class="tile">{{ ch }}</span>
    </div>

    <div v-if="phase === 'waiting'" class="statusRow">
      <span class="liveDot" />
      <span class="statusText">Waiting for you to enter the code…</span>
      <span class="countdown">{{ mmss }}</span>
    </div>

    <div v-else-if="phase === 'verified'" class="statusRow">
      <AppIcon name="check_circle" :size="16" color="var(--color-success)" />
      <span class="statusText verified">Number verified.</span>
    </div>

    <div v-else-if="phase === 'failed'" class="failRow">
      <AppIcon name="error" :size="16" color="var(--color-danger)" />
      <span class="statusText">{{ failCopy }}</span>
    </div>

    <div class="cacActions">
      <button
        v-if="phase === 'failed'"
        type="button"
        class="btn primary"
        @click="$emit('recall')"
      >
        <AppIcon name="phone_call" :size="14" />Recall
      </button>
      <button
        v-if="phase !== 'verified'"
        type="button"
        class="btn secondary"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../../components/AppIcon.vue'
import { formatPhone } from '../../../utils/phone'

// Pure presentational: the parent owns the verify state (useTelephony().verify)
// and wires cancel/recall.
const props = defineProps<{
  phase: 'calling' | 'waiting' | 'failed' | 'verified'
  phoneNumber: string
  code: string
  secondsLeft: number
  failReason: '' | 'generic' | 'timeout'
}>()

defineEmits<{ (e: 'cancel'): void; (e: 'recall'): void }>()

const prettyPhone = computed(() => formatPhone(props.phoneNumber) || props.phoneNumber)

const headline = computed(() => {
  if (props.phase === 'verified') return 'Verified — you’re all set.'
  if (props.phase === 'failed') return 'That call didn’t connect.'
  if (props.phase === 'calling') return `Calling ${prettyPhone.value}…`
  return `We’re calling ${prettyPhone.value} now`
})

const subline = computed(() => {
  if (props.phase === 'verified') return 'Callbacks to your pool numbers will reach this phone.'
  if (props.phase === 'failed') return 'Give it another try — we’ll place a fresh call with a new code.'
  return 'Pick up and enter this code on your keypad.'
})

const codeChars = computed(() => {
  const raw = String(props.code || '')
  const padded = raw.padEnd(6, ' ').slice(0, Math.max(6, raw.length))
  return padded.split('')
})

const mmss = computed(() => {
  const s = Math.max(0, props.secondsLeft)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, '0')}`
})

const failCopy = computed(() =>
  props.failReason === 'timeout'
    ? 'The code timed out before it was entered.'
    : 'We couldn’t verify that number.',
)
</script>

<style scoped>
.cac {
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cacHead { display: flex; flex-direction: column; gap: 4px; }
.cacTitle { font-size: var(--fs-base); font-weight: var(--fw-semibold); }
.cacSub { font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }

.tiles {
  display: flex;
  justify-content: center;
  gap: 8px;
  background: var(--color-bg-app);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-neu-in-cream);
  padding: 18px 12px;
}
.tile {
  width: 44px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-surface);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  font-family: var(--font-mono);
  font-size: 26px;
  font-weight: var(--fw-medium);
}

.statusRow, .failRow { display: flex; align-items: center; gap: 9px; }
.liveDot {
  width: 8px; height: 8px; border-radius: var(--radius-full); background: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}
.statusText { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--color-text-secondary); }
.statusText.verified { color: var(--color-success); }
.countdown { margin-left: auto; font-family: var(--font-mono); font-size: var(--fs-sm); color: var(--color-text-tertiary); }

.cacActions { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-3); }
</style>
